import type { CmmdBillingContext } from "@cmmd-forge/contracts";
import { formatDayShort } from "@cmmd-forge/shared/usageFormat";

import { useBilling } from "../../usage/useBilling";
import { PROVIDER_SETTINGS_ICON_BY_PROVIDER } from "../providerSettingsIcons";
import { Skeleton } from "../ui/skeleton";
import { buildMeterRows, type BillingMeterRow } from "./billingMeters";
import { UsageCard, UsageCardRefreshButton, UsageMeterBar } from "./usageCard";

const CmmdIcon = PROVIDER_SETTINGS_ICON_BY_PROVIDER.cmmd;

/**
 * What the CMMD plan has consumed this period, and what is left.
 *
 * Sits above the provider grid rather than inside it because it carries every
 * allowance the plan meters, where a provider account carries two windows and a
 * balance. In a grid cell those rows would run past the fold; full width lets
 * them pair off into two columns and keeps the card roughly a provider card
 * tall.
 *
 * The period is the subscription's own, not the window selector the estimate
 * section offers: an allowance resets on a billing date, so "last 7 days" of it
 * would answer a question nobody is asking.
 */
export function CmmdPlanCard() {
  const { billing, isPending, isRefreshing, error, refresh } = useBilling();

  if (isPending) return <CmmdPlanCardSkeleton />;

  const rows = billing === null ? [] : buildMeterRows(billing);

  return (
    <UsageCard
      action={
        <UsageCardRefreshButton
          isDisabled={false}
          isRefreshing={isRefreshing}
          label="Refresh CMMD plan"
          onRefresh={refresh}
          tooltip={isRefreshing ? "Refreshing" : "Refresh CMMD plan usage"}
        />
      }
      icon={CmmdIcon ? <CmmdIcon aria-hidden="true" className="mt-0.5 size-5 shrink-0" /> : null}
      kind="cmmd-plan"
      subtitle={billing === null ? "Plan unavailable" : describePlanPeriod(billing)}
      title={billing === null ? "CMMD" : `CMMD · ${billing.planName}`}
    >
      <CmmdPlanCardBody error={error} rows={rows} />
    </UsageCard>
  );
}

function CmmdPlanCardBody({
  error,
  rows,
}: {
  readonly error: string | null;
  readonly rows: readonly BillingMeterRow[];
}) {
  // A card that vanishes on a failed read says "you have no CMMD plan", which
  // is a different and wrong statement from "I could not read the plan you
  // have". The provider cards answer a dead account the same way.
  if (error !== null) return <p className="text-muted-foreground text-sm">{error}</p>;

  if (rows.length === 0) {
    return <p className="text-muted-foreground text-sm">This plan has no metered allowances.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-x-8">
        {rows.map((row) => (
          <PlanMeterRow key={row.key} row={row} />
        ))}
      </div>
      {/* The page carries two numbers that both look like money. Someone who
          reads the estimate as a bill will think they were overcharged by
          orders of magnitude, so each quantity names the other. */}
      <span className="text-muted-foreground text-xs">
        Metered consumption against your CMMD plan this period. What the same work would have cost
        at API rates is under Estimated value.
      </span>
    </div>
  );
}

/**
 * Status, seats, the Environment entitlement, and the period, in one line the
 * reader can scan.
 *
 * `environments.total` is what the plan includes plus what was bought on top,
 * not how many Environments the organization is running: CMMD's billing
 * control plane derives it from the subscription. Say "included" so a plan
 * with none does not read as a workspace with none (FOR-36406).
 */
export function describePlanPeriod(billing: CmmdBillingContext): string {
  const environments = billing.environments.total;
  const parts = [
    billing.subscriptionStatus.replace(/_/g, " "),
    `${billing.seatQuantity} ${billing.seatQuantity === 1 ? "seat" : "seats"}`,
    `${environments} ${environments === 1 ? "Environment" : "Environments"} included`,
  ];

  if (billing.currentPeriodStart !== null && billing.currentPeriodEnd !== null) {
    parts.push(
      `${formatDayShort(billing.currentPeriodStart.slice(0, 10))} to ${formatDayShort(
        billing.currentPeriodEnd.slice(0, 10),
      )}`,
    );
  }

  if (billing.cancelAtPeriodEnd) parts.push("cancels at period end");

  return parts.join(" · ");
}

/**
 * One allowance.
 *
 * No explanatory hint under the figure. Six of those fit a section of their
 * own; in a card above two provider cards they are the density problem, and
 * `250K / 1M` with `750K left` under it carries itself.
 */
function PlanMeterRow({ row }: { readonly row: BillingMeterRow }) {
  return (
    <div className="flex flex-col gap-1.5" data-plan-meter-row={row.key}>
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-foreground text-sm">{row.label}</span>
        <span className="text-foreground text-sm tabular-nums">
          {row.usedLabel}
          {row.limitLabel === null ? "" : ` / ${row.limitLabel}`}
        </span>
      </div>
      {/* An unlimited meter has no share to fill, but it sits beside one that
          does. Dropping the bar entirely lifts this row's caption a bar's
          height above its neighbour's, so the space is kept and left empty. */}
      {row.share === null ? (
        <div aria-hidden="true" className="h-1" />
      ) : (
        <UsageMeterBar share={row.share} />
      )}
      <span className="text-muted-foreground text-xs" data-plan-meter-caption={row.key}>
        {row.remainingLabel === null ? "Unlimited." : `${row.remainingLabel} left.`}
      </span>
    </div>
  );
}

function CmmdPlanCardSkeleton() {
  return <Skeleton className="h-44 w-full" />;
}
